import type { Appointment, Reference } from '@medplum/fhirtypes';
import {
  APPOINTMENT_GROUP_EXT,
  GROUP_SHARED_NOTE_EXT,
} from '@unify/fhir-model/constants';

function getReferenceExtension(
  resource: { extension?: { url?: string; valueReference?: Reference }[] },
  url: string,
): Reference | undefined {
  return resource.extension?.find((e) => e.url === url)?.valueReference;
}

function setReferenceExtension<T extends { extension?: Appointment['extension'] }>(
  resource: T,
  url: string,
  ref: Reference | undefined,
): T {
  const others = (resource.extension ?? []).filter((e) => e.url !== url);
  const extension = ref
    ? [...others, { url, valueReference: ref }]
    : others;
  return {
    ...resource,
    extension: extension.length ? extension : undefined,
  };
}

/** Shared Group QuestionnaireResponse linked from an Appointment. */
export function getAppointmentSharedNoteRef(
  appointment: Appointment,
): Reference | undefined {
  return getReferenceExtension(appointment, GROUP_SHARED_NOTE_EXT);
}

export function setAppointmentSharedNoteRef(
  appointment: Appointment,
  ref: Reference | undefined,
): Appointment {
  return setReferenceExtension(appointment, GROUP_SHARED_NOTE_EXT, ref);
}

/**
 * True when the Appointment is a cohort-backed Group Therapy session
 * (Group in supportingInformation or appointment-group extension).
 * Does not include ad-hoc multi-patient calendar sessions.
 */
export function isCohortGroupAppointment(appointment: Appointment): boolean {
  if (
    appointment.supportingInformation?.some((r) => r.reference?.startsWith('Group/'))
  ) {
    return true;
  }
  return appointment.extension?.some((e) => e.url === APPOINTMENT_GROUP_EXT) ?? false;
}

/** Group reference for a cohort appointment (supportingInformation, else appointment-group ext). */
export function getAppointmentGroupRef(appointment: Appointment): Reference | undefined {
  const fromSupporting = appointment.supportingInformation?.find((r) =>
    r.reference?.startsWith('Group/'),
  );
  if (fromSupporting?.reference) {
    return {
      reference: fromSupporting.reference,
      display: fromSupporting.display,
    };
  }
  return getReferenceExtension(appointment, APPOINTMENT_GROUP_EXT);
}
